import CardCommon from '@op/components/CardCommon';
import { Checkbox } from 'tea-component';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import {
  tableIcon,
  lineIcon,
  barIcon,
  pieIcon,
  stackLineIcon,
  SymbolType,
  vsIcon,
} from '@op/constants/chart';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useHistory, useIntl, getLocale } from 'umi';
import {
  ByTypeSelectType,
  FILTER_HOUR_LIST,
  MentionsSentimentTabType,
  sentimentMentionsOneLevelOptionsMap,
} from '../../constants';
import { gameInfoType } from '@op/types/common';
import Charts from './Charts';
import {
  getRenderList,
  mentionsKeyMetricsValueKeyMap,
  MULTI_REGION_TITLE,
  TREND_TEXT,
  sentimentMentionsMobileTowLevelOptions,
  sentimentMentionsPCTowLevelOptions,
  SentimentMentionsTwoLevelType,
} from './utils';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { useKeyMetricsMentionsChart } from '../../hooks';
import { arrayToSimpleObj, initialUpperCase } from '@op/utils';
import { goToCompetitorKeyMetrics } from '@op/utils/url';
import { buriedBtnPoint, DBO_POINT } from '@op/operationLog/buriedPoint';

import EventSetting from '../components/EventSetting';
import EventTypeSetting from '../components/EventTypeSetting';
import SvgIcon from '@home/components/SvgIcon';
import BubbleCommon, {
  commonEventSettingTips,
  keyMetricsByMentionsTypeConsoleTips,
  keyMetricsByMentionsTypeMobileTips,
  keyMetricsByMentionsTypePCTips,
  keyMetricsShowEventTips,
  eventTypeFilterTips,
} from '@op/components/BubbleCommon';
import { AnalysisSummaryTypeTab } from '@op/pages/AIAnalysisHub/KeywordAndURLAnalysis/types';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { useHighPriorityEvents } from './hooks/useHighPriorityEvents';
import type { IHighPriorityEventItem } from '@op/components/ChartsEvents/types';

// SplitPane 分栏组件
import SplitPane, { SPLIT_PANE_PRESETS } from '@op/components/SplitPane';
import EventDetailSidebar from './EventDetailSidebar';
import { useEventDetail } from './EventDetailSidebar/hooks/useEventDetail';
import type {
  IEventTypeFilter,
  IDetailQueryParams,
} from './EventDetailSidebar/types';
import './index.scss';
import { useFrontWebConfig } from '@op/hooks/useFrontWebConfig';

export default function MetricsChart({
  tab,
  filter,
  gameInfo,
}: {
  tab: MentionsSentimentTabType | AnalysisSummaryTypeTab;
  filter: any;
  gameInfo: gameInfoType;
}) {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const locale = getLocale();
  const { download, downloading } = useDownloadParams();
  const [mentions, setMentions] =
    useState<ByTypeSelectType>('by_mentions_type');
  const [metrics, setMetrics] = useState<{
    value: SentimentMentionsTwoLevelType;
  }>({
    value: 'mentions_brand_health',
  });

  const [oneOptions, setOneOptions] = useState<
    Array<{ text: string; value: string }>
  >([]);
  const [type, setType] = useState<SymbolType>('bar');

  const byMentionsTipsMap = useMemo(() => {
    if (gameInfo.entity_type === 'mobile') {
      return keyMetricsByMentionsTypeMobileTips;
    }
    if (gameInfo.entity_type === 'pc') {
      return keyMetricsByMentionsTypePCTips;
    }
    if (gameInfo.entity_type === 'console') {
      return keyMetricsByMentionsTypeConsoleTips;
    }

    return undefined;
  }, [gameInfo.entity_type]);

  useEffect(() => {
    const options = sentimentMentionsOneLevelOptionsMap[tab].filter(
      ({ pcFilter }) => (gameInfo.entity_type === 'mobile' ? true : !pcFilter),
    );
    setOneOptions(options);
    if (!options.find((val) => val.value === mentions)) {
      setMentions(options[0].value);
    }
    // setMentions(options[0].value);
    // 接口请求全靠metrics字段来驱动，所以这里重复赋值，保证tab切换和下拉切换的时候能生效
    setMetrics({ ...metrics });
  }, [byMentionsTipsMap, gameInfo, tab]);

  const optionsList = useMemo(() => {
    return oneOptions.map((val) => {
      const { value, text } = val;
      if (value === 'by_mentions_type') {
        return {
          value,
          text: (
            <BubbleCommon
              tipsMap={byMentionsTipsMap}
              placement={'right'}
              childStyle={{ width: '100%', paddingRight: '12px' }} // 让气泡展示在选项外
            >
              {formatMessage({ id: text })}
            </BubbleCommon>
          ),
        };
      }
      return {
        value,
        text: formatMessage({ id: text }),
      };
    });
  }, [formatMessage, oneOptions]);

  const [twoLevelOptions, twoLevelOptionsMap] = useMemo(() => {
    let options =
      gameInfo.entity_type === 'mobile'
        ? sentimentMentionsMobileTowLevelOptions(formatMessage, filter)
        : sentimentMentionsPCTowLevelOptions(formatMessage);

    if (gameInfo.entity_type === 'console') {
      options = options.filter((val) => val.value !== 'mentions_PCU');
    }

    if (filter?.date_type === 'hourly') {
      options = options.filter(
        (val) => !!!FILTER_HOUR_LIST.includes(val.value),
      );
      if (FILTER_HOUR_LIST.includes(metrics.value)) {
        setMetrics({
          value: options[0].value as SentimentMentionsTwoLevelType,
        });
      }
    }
    const optionsMap = arrayToSimpleObj(options, 'rightTitle');

    return [options, optionsMap];
  }, [gameInfo, formatMessage, filter?.date_type]);

  const hideMetricsSelect = ['pie', 'line', 'stack_line'].includes(type);

  const params = useMemo(() => {
    const realMentions =
      mentions === 'by_rating_ratio' ? 'by_rating' : mentions;
    const obj = {
      ...filter,
      metrics: [metrics.value],
      tab_type: tab,
      games: [],
      by_type: realMentions,
      ext_by_type: mentionsKeyMetricsValueKeyMap[metrics.value],
    };
    return obj;
  }, [filter, metrics, mentions]);

  const renderKeysList = getRenderList({
    currentTab: tab,
    gameInfo,
    by_type: mentions,
    key_metrics: metrics.value,
    need_all_list: true,
  });

  const isRatingRatio = mentions === 'by_rating_ratio';

  const { data, loading, eventData, chartEventSetting, changeEventSetting } =
    useKeyMetricsMentionsChart({
      filter: params,
      extraData: renderKeysList,
      isRatingRatio,
    });

  function changeChartType(type: any) {
    if (type === 'vs') {
      goToCompetitorKeyMetrics(history, filter);
    } else {
      setType(type || 'bar');
    }
  }

  const [transVisible, setTransVisible] = useState(false);
  const [eventTypeSettingVisible, setEventTypeSettingVisible] = useState(false);

  const chartLeftTitle = useMemo(() => {
    const dateType = filter?.date_type || 'daily';
    return formatMessage(
      { id: 'Mentions ({dateType})' },
      { dateType: formatMessage({ id: initialUpperCase(dateType) }) },
    );
  }, [filter, formatMessage]);

  const HasHightEvent = useFrontWebConfig({
    keys: 'has_event_attribution',
  });

  // 事件详情数据请求（提升到页面级，缓存生命周期与页面一致）
  // autoFetch: false + queryParams: null → 不自动请求，由 useHighPriorityEvents 通过 fetchEventDetailData 主动触发
  const {
    fetchData: fetchEventDetailData,
    eventDetails,
    activeRegion,
    setActiveRegion,
    loading: eventDetailLoading,
    empty: eventDetailEmpty,
    hasMultipleRegions,
    hoverEventDetails,
    hoverLoading,
  } = useEventDetail();

  // 高优先级事件状态管理（包含事件数据、选中状态、侧边栏、点击事件处理）
  // 传入 fetchEventDetailData，在 handleDetailOpen 内部直接调用以触发请求
  const {
    highPriorityEvents,
    selectedEvent,
    sidebarVisible,
    closeSidebar,
    handleEventClickWithTracking,
    handleBarClick,
    handleCloseSidebar,
  } = useHighPriorityEvents(
    { filter },
    HasHightEvent,
    fetchEventDetailData,
    setActiveRegion,
  );

  // hover 气泡时预请求事件详情数据（预热缓存，点击时瞬间展示）
  const handleBubbleHoverFetch = useCallback(
    (event: IHighPriorityEventItem) => {
      const { start_time, end_time, overview_title, title, tagName } = event;
      if (!start_time || !end_time) return;

      const params: IDetailQueryParams = {
        source: 'event',
        edition_unified_id: filter.edition_unified_id,
        start_time: moment(start_time)
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ss'),
        end_time: moment(end_time).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        overview_title: overview_title || title,
        data_type: 'overview',
        region: tagName,
      };
      // silent 模式：只预热缓存，不更新 UI 状态（侧边栏不受 hover 影响）
      fetchEventDetailData(params, { silent: true });
    },
    [filter.edition_unified_id, fetchEventDetailData],
  );

  // 事件类型筛选状态（新增）
  // 默认启用筛选，默认全选（selectedTypes 为 null 表示全选）
  const [eventTypeFilter, setEventTypeFilter] = useState<IEventTypeFilter>({
    enabled: true,
    selectedTypes: null,
  });

  useEffect(() => {
    closeSidebar();
  }, [params]);

  // 根据 eventTypeFilter 筛选后的高优先级事件（用于 Charts 展示）
  // 复选框勾选：启用筛选，根据 selectedTypes 展示对应事件
  // 复选框取消：禁用筛选，不展示任何高级事件
  // selectedTypes null 表示全选，空数组表示无选中，非空数组表示部分选中
  const filteredHighPriorityEvents = useMemo(() => {
    if (!eventTypeFilter.enabled) {
      // 未启用筛选时，不展示任何高级事件
      return {};
    }
    if (eventTypeFilter.selectedTypes === null) {
      // null 表示全选，展示所有事件
      return highPriorityEvents;
    }
    // 非空数组：根据 selectedTypes 筛选；空数组：无选中，返回空对象
    const filtered: Record<string, IHighPriorityEventItem[]> = {};
    Object.entries(highPriorityEvents).forEach(([date, events]) => {
      const filteredEvents = events.filter((event) =>
        eventTypeFilter.selectedTypes!.includes(event.id),
      );
      if (filteredEvents.length > 0) {
        filtered[date] = filteredEvents;
      }
    });
    return filtered;
  }, [highPriorityEvents, eventTypeFilter]);

  const { Competitor_Rank_Hide } = useGetSingePageSourceRule({
    keys: ['Competitor_Rank_Hide'],
  });
  const operations = useMemo(() => {
    const list = [tableIcon, lineIcon, stackLineIcon, barIcon];

    if (!isRatingRatio) {
      list.unshift(pieIcon);
    }

    if (!Competitor_Rank_Hide) {
      list.unshift(vsIcon);
    }

    if (selectedEvent !== null) {
      return list.filter(
        (item) => !['vs', 'table', 'pie'].includes(item.symbol),
      );
    }

    return list;
  }, [mentions, selectedEvent]);

  return (
    <SplitPane
      {...SPLIT_PANE_PRESETS.eventDetail}
      sidebarVisible={sidebarVisible}
      sidebar={
        <EventDetailSidebar
          visible={sidebarVisible}
          onClose={handleCloseSidebar}
          multiRegionTitle={MULTI_REGION_TITLE}
          eventDetails={eventDetails}
          activeRegion={activeRegion}
          setActiveRegion={setActiveRegion}
          loading={eventDetailLoading}
          empty={eventDetailEmpty}
          hasMultipleRegions={hasMultipleRegions}
        />
      }
    >
      <CardCommon
        // contentCard
        className="dbo-card-common dbo-card-common--by-sentiment mentions-sentiment-card"
        hasDownload
        downloadLoading={downloading}
        operationInfo={[type, changeChartType]}
        operations={operations}
        handleDownloadOnClick={() => {
          download({
            key: 'KeyMetricsMentionsSentiment',
            fileName: 'Key Metrics Mentions Sentiment',
            filter,
            auxiliaryParams: {
              currentTab: tab,
              gameInfo,
              by_type: mentions,
              metrics: metrics.value,
              isRatingRatio,
            },
            data: data.tableData,
          });
        }}
        title={
          <div className="dbo-card-selects">
            <SortBySelect
              defaultValue={mentions}
              onChange={(key: string, value: any) => {
                buriedBtnPoint('KMMS_ByMetricsType', value);
                setMentions(value);
              }}
              defaultParams={{
                options: optionsList,
                needFormatMessage: false,
                btnTextPrefix: formatMessage({ id: 'Mentions' }),
                matchButtonWidth: true,
                dealwithButton: (value: any, { textMap }: any) => {
                  return textMap[value];
                },
              }}
            />
            {!hideMetricsSelect && (
              <SortBySelect
                defaultValue={metrics.value}
                onChange={(_key: string, value: any) => {
                  buriedBtnPoint('KMMS_Metrics', value);
                  setMetrics({ value });
                }}
                defaultParams={{
                  options: twoLevelOptions,
                  btnTextPrefix: formatMessage({ id: 'Metrics' }),
                  matchButtonWidth: true,
                  needFormatMessage: false,
                  dealwithButton: (value: any, { textMap }: any) => {
                    return textMap[value];
                  },
                }}
              />
            )}
            {['bar', 'line', 'stack_line'].includes(type) && (
              <div className="dbo-events-setting">
                <div className="dbo-events-setting-inner">
                  <Checkbox
                    value={
                      chartEventSetting.showEventAxis ||
                      chartEventSetting.showEventBubbles
                    }
                    onChange={(value) => {
                      changeEventSetting({
                        showEventAxis: value,
                        showEventBubbles: value,
                        bubbleEvent: chartEventSetting.bubbleEvent,
                      });
                    }}
                  >
                    <BubbleCommon
                      showIcon={false}
                      tipsMap={keyMetricsShowEventTips}
                    >
                      <span>{formatMessage({ id: 'Show Events' })}</span>
                    </BubbleCommon>
                    <BubbleCommon
                      placement="top"
                      childStyle={{ verticalAlign: 'middle' }}
                      tipsMap={commonEventSettingTips}
                    ></BubbleCommon>
                  </Checkbox>
                  <span
                    className="dbo-events-setting-icon"
                    onClick={() => {
                      setTransVisible(true);
                      buriedBtnPoint('KMLG_EventSetting');
                    }}
                  >
                    <SvgIcon type="jichushezhi" />
                  </span>
                </div>

                {/* 事件类型筛选按钮（hourly/monthly 不支持高级事件，不展示） */}
                {filter?.date_type !== 'hourly' &&
                  filter?.date_type !== 'monthly' &&
                  HasHightEvent && (
                    <BubbleCommon
                      showIcon={false}
                      tipsMap={eventTypeFilterTips}
                    >
                      <div className="dbo-events-setting-inner">
                        <Checkbox
                          value={eventTypeFilter.enabled}
                          onChange={(value) => {
                            DBO_POINT.btn('KMMS_EventSelectBox');
                            setEventTypeFilter({
                              ...eventTypeFilter,
                              enabled: value,
                            });
                          }}
                        >
                          <span>
                            {formatMessage({ id: 'Show Anomaly Attribution' })}
                          </span>
                        </Checkbox>
                        <span
                          className="dbo-events-setting-icon"
                          onClick={() => {
                            setEventTypeSettingVisible(true);
                            DBO_POINT.btn('KMMS_EventTypeSetting');
                          }}
                        >
                          <SvgIcon type="jichushezhi" />
                        </span>
                      </div>
                    </BubbleCommon>
                  )}
              </div>
            )}
            {!loading && (
              <>
                <EventSetting
                  data={eventData.eventList}
                  visible={transVisible}
                  timeFormat={eventData.format}
                  confirm={(data: {
                    bubbleEvent: { [key: string]: Array<any> };
                    showEventAxis: boolean;
                    showEventBubbles: boolean;
                  }) => {
                    setTransVisible(false);

                    changeEventSetting(data);
                  }}
                  close={() => setTransVisible(false)}
                  xAxisData={data?.xAxisData || []}
                  dateType={filter.date_type}
                  showEventAxisValue={chartEventSetting.showEventAxis}
                  showEventBubblesValue={chartEventSetting.showEventBubbles}
                ></EventSetting>
                {/* 事件类型筛选弹窗（新增） */}
                <EventTypeSetting
                  highPriorityEvents={highPriorityEvents}
                  visible={eventTypeSettingVisible}
                  selectedTypes={eventTypeFilter.selectedTypes}
                  confirm={(selectedTypes) => {
                    setEventTypeFilter({ ...eventTypeFilter, selectedTypes });
                  }}
                  close={() => setEventTypeSettingVisible(false)}
                  filter={filter}
                />
              </>
            )}
          </div>
        }
        loading={loading}
        loadingHeight={390}
        // empty={data.empty}
        logBtnId="KMMS"
      >
        <Charts
          tab={tab}
          gameInfo={gameInfo}
          by_type={mentions}
          type={type}
          filter={filter}
          chartEventSetting={chartEventSetting}
          HasHightEvent={HasHightEvent}
          eventData={eventData}
          metrics={metrics.value}
          rightTitle={
            hideMetricsSelect ? '' : twoLevelOptionsMap[metrics.value]
          }
          sidebarVisible={sidebarVisible}
          isRatingRatio={isRatingRatio}
          hideMetricsSelect={hideMetricsSelect}
          data={data}
          leftTitle={chartLeftTitle}
          handleEventClick={handleEventClickWithTracking}
          highPriorityEvents={filteredHighPriorityEvents}
          onBarClick={handleBarClick}
          eventTypeFilter={eventTypeFilter}
          blockTitle={MULTI_REGION_TITLE}
          trendText={TREND_TEXT}
          onBubbleHoverFetch={handleBubbleHoverFetch}
          hoverEventDetails={hoverEventDetails}
          hoverLoading={hoverLoading}
        ></Charts>
      </CardCommon>
    </SplitPane>
  );
}

